/**
 * level5Session.ts
 * Per-level session helpers for World 5 — Eclipse Bastion (greybox).
 *
 * Thin config layer over {@link createWorldSession}. World 5 has no
 * per-stage ability unlock (the air-dash unlock fires on the 4-B clear,
 * and there is no World 6 to gate), so this is the simplest of the three
 * homogeneous worlds: the four main stages (5-1..5-4) chain in order into
 * the Star-Eater boss arena (5-B), and the secret stage (5-0) is
 * registered but deliberately outside the progression chain.
 *
 * The named exports below preserve the original public surface so the
 * dispatcher and tests are untouched; the shared bodies live in
 * `worldSessionFactory.ts`.
 *
 * @module level5Session
 */

import {
  createWorldSession,
  type WorldSession,
  type WorldSessionConfig,
} from './worldSessionFactory';
import type { PlayScene } from '../scenes/PlayScene';

/** Shape returned by {@link getWorld5Session} — alias of the shared bundle. */
export type World5Session = WorldSession;

// DECISION: the id→number and next-chain maps stay explicit (same shape
// as W3/W4) so the factory's hasOwnProperty guards keep rejecting
// prototype keys. Secret stage 5-0 → integer 0, boss arena 5-B → integer
// 5, matching the LevelRegistry convention.
//
// DECISION: no `abilityUnlock` here, unlike W3 (wall slide) and W4 (air
// dash). World 5 is the final world — there is no World 6 to gate and
// ADR-009 assigns no ability to a 5-B clear. An empty unlock would be a
// silent no-op call site rather than documentation.
const CONFIG: WorldSessionConfig = {
  worldId: 5,
  worldLabel: 'World 5',
  idToLevelNumber: {
    '5-0': 0,
    '5-1': 1,
    '5-2': 2,
    '5-3': 3,
    '5-4': 4,
    '5-B': 5,
  },
  fallbackId: '5-1',
  nextChain: {
    '5-1': '5-2',
    '5-2': '5-3',
    '5-3': '5-4',
    '5-4': '5-B',
    // 5-B is the last stage of the game → resolves to `null` (bounce back
    // to the world map). The secret stage 5-0 is absent from the chain by
    // design.
  },
};

const helpers = createWorldSession(CONFIG);

/**
 * Resolve a `?level=` query value to a canonical World 5 session id.
 * Falls back to `'5-1'` for any unknown or missing input.
 *
 * @param raw - Raw param value from the router.
 * @returns A valid session id string.
 */
export function resolveWorld5LevelId(raw: string | string[] | undefined): string {
  return helpers.resolveLevelId(raw);
}

/**
 * Build a session bundle for the given World 5 level id.
 *
 * @param levelId - Canonical id (`'5-1'`..`'5-4'`, `'5-B'`, or `'5-0'` secret).
 * @returns A World5Session with everything `scene.init` needs.
 * @throws If the level id is not registered.
 */
export function getWorld5Session(levelId: string): World5Session {
  return helpers.getSession(levelId);
}

/**
 * Initialise the PlayScene from a World 5 session bundle. No finale
 * wiring — main W5 stages exit on right-edge collision, and the 5-B boss
 * arena clears through the shared `bossArena` path (right-edge exit is
 * suppressed while the Star-Eater lives).
 *
 * @param scene - The PlayScene to initialise.
 * @param session - The session bundle returned by {@link getWorld5Session}.
 * @param skipCheckpointRestore - When true, respawn at the level start.
 */
export function initWorld5Scene(
  scene: PlayScene,
  session: World5Session,
  skipCheckpointRestore = false,
): void {
  helpers.initScene(scene, session, skipCheckpointRestore);
}

/**
 * Return the next World 5 level id in normal-progression order, or
 * `null` if the player is at the last shipped stage.
 *
 * @param levelId - Current level id.
 * @returns The next level id, or `null` if there is none.
 */
export function getNextWorld5LevelId(levelId: string): string | null {
  return helpers.getNextLevelId(levelId);
}

/**
 * Persist the just-completed World 5 level's collected ids and score.
 * Fire-and-forget: failures are logged, never thrown.
 *
 * @param levelId - Save key for the completed level.
 */
export async function persistWorld5Completion(levelId: string): Promise<void> {
  await helpers.persistCompletion(levelId);
}

/**
 * Canonical id of the final stage of the game — the Star-Eater arena.
 *
 * DECISION: declared here rather than in a constants file because this
 * module already owns World 5's chain, and the finale is defined by
 * being the terminus of that chain. Keeping the two together means a
 * future re-ordering of World 5 cannot leave the ending pointing at a
 * stage that is no longer last.
 */
export const FINALE_LEVEL_ID = '5-B';

/**
 * Whether clearing `levelId` finishes the game.
 *
 * Deliberately an id match rather than a `getNextWorld5LevelId() === null`
 * check: the World 5 secret stage `5-0` also has no successor, but
 * clearing it must not roll the credits.
 *
 * @param levelId - Level id that was just completed.
 * @returns True only for the final stage of the game.
 */
export function isFinaleLevelId(levelId: string): boolean {
  return levelId === FINALE_LEVEL_ID;
}
